import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Text,
} from '../../../utils/chakraMonomorphic'

import copy from '../copy.json'
import {ReportFilterSelection} from '../../../types'

export interface FilterOption {
  id: number
  name: string
}

interface Props {
  sessions: FilterOption[]
  divisions: FilterOption[]
  bunkPlans: FilterOption[]
  isLoading: boolean
  isRunning: boolean
  value: ReportFilterSelection
  onChange: (next: ReportFilterSelection) => void
  onSubmit: () => void
}

const toggle = (ids: number[], id: number): number[] =>
  ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id]

interface MultiSelectListProps {
  /** Used to build this list's test ids, e.g. 'sessions' -> '…-sessions-clear'. */
  testIdPrefix: string
  label: string
  options: FilterOption[]
  selectedIds: number[]
  isDisabled: boolean
  /** Statements are hidden while options arrive, so a load is not reported as an empty season. */
  isLoading: boolean
  /** AC-22 — what an empty selection means, stated rather than inferred. */
  allHint: string
  /** AC-23 — what an empty *list* means, which is a different fact. */
  emptyStatement: string
  onSelectedIdsChange: (next: number[]) => void
}

/*
  One checkbox list filter, with the bulk controls and the two statements
  AC-20..AC-23 require.

  The bulk controls sit above the scrolling Box so they stay reachable when a
  thirty-option list is scrolled, and are type='button' because the enclosing
  ReportFilters is a form whose submit runs the report — a defaulted button
  inside it would print instead of selecting.

  onSelectedIdsChange takes only this list's ids: the caller owns folding them
  back into the whole selection, so a bulk action here cannot disturb the other
  list or the bunk plan.
*/
const MultiSelectList: React.FC<MultiSelectListProps> = ({
  testIdPrefix,
  label,
  options,
  selectedIds,
  isDisabled,
  isLoading,
  allHint,
  emptyStatement,
  onSelectedIdsChange,
}): React.ReactElement => {
  const testId = (suffix: string): string =>
    `tagged-photo-report-${testIdPrefix}-${suffix}`

  return (
    <FormControl as='fieldset' maxW='260px'>
      <FormLabel as='legend' fontSize='sm'>
        {label}
      </FormLabel>

      {/*
        AC-23 — bulk actions exist only where there is something to act on. A
        pair of buttons over an empty list looks operable and does nothing.
      */}
      {options.length > 0 && (
        <Flex gap={3} mb={1}>
          <Button
            type='button'
            variant='link'
            size='sm'
            isDisabled={isDisabled}
            data-testid={testId('select-all')}
            onClick={(): void =>
              onSelectedIdsChange(options.map((option) => option.id))
            }>
            {copy.filters.selectAll}
          </Button>
          <Button
            type='button'
            variant='link'
            size='sm'
            isDisabled={isDisabled}
            data-testid={testId('clear')}
            onClick={(): void => onSelectedIdsChange([])}>
            {copy.filters.clearSelection}
          </Button>
        </Flex>
      )}

      {/*
        AC-22 — the "empty means all campers" default, said out loud. It shows
        whether or not the list has options, because the default is the same
        either way; the shipped code only ever showed it for an empty list,
        which is why a long-list camp never saw it.
      */}
      {selectedIds.length === 0 && !isLoading && (
        <Text fontSize='sm' color='gray.700' data-testid={testId('all-hint')}>
          {allHint}
        </Text>
      )}

      {options.length === 0 && !isLoading && (
        <Text fontSize='sm' color='gray.700' data-testid={testId('empty')}>
          {emptyStatement}
        </Text>
      )}

      <Box maxH='220px' overflowY='auto'>
        {options.map((option) => (
          <Checkbox
            key={option.id}
            display='flex'
            isDisabled={isDisabled}
            isChecked={selectedIds.includes(option.id)}
            onChange={(): void =>
              onSelectedIdsChange(toggle(selectedIds, option.id))
            }>
            {option.name}
          </Checkbox>
        ))}
      </Box>
    </FormControl>
  )
}

/**
 * The three season-scoped filters (AC-3).
 *
 * There is deliberately no season control: the run is always scoped to the
 * camp's current season, resolved from the camp's own record (AC-4).
 *
 * An empty session or division selection means "all of them" rather than
 * "none" — a report with no rows is not a useful default, and AC-12 already
 * covers the case where a real selection matches nobody. AC-22 makes that
 * default visible; it does not change it.
 */
export const ReportFilters: React.FC<Props> = ({
  sessions,
  divisions,
  bunkPlans,
  isLoading,
  isRunning,
  value,
  onChange,
  onSubmit,
}): React.ReactElement => {
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    onSubmit()
  }

  // AC-21 — one condition for every control, so a bulk action can never be
  // live while the list beneath it is not.
  const isBusy = isLoading || isRunning

  return (
    <form onSubmit={handleSubmit}>
      <Text fontSize='1.05rem' fontWeight='bold' mb={2}>
        {copy.filters.heading}
      </Text>

      {isLoading && (
        <Flex
          alignItems='center'
          gap={3}
          mb={4}
          data-testid='tagged-photo-report-filters-loading'>
          <Spinner size='sm' />
          <Text fontSize='sm'>{copy.filters.loading}</Text>
        </Flex>
      )}

      <Flex gap={10} wrap='wrap' mb={6}>
        <MultiSelectList
          testIdPrefix='sessions'
          label={copy.filters.sessionsLabel}
          options={sessions}
          selectedIds={value.sessionIds}
          isDisabled={isBusy}
          isLoading={isLoading}
          allHint={copy.filters.allSessionsHint}
          emptyStatement={copy.filters.sessionsEmpty}
          onSelectedIdsChange={(sessionIds): void =>
            onChange({...value, sessionIds})
          }
        />

        <MultiSelectList
          testIdPrefix='divisions'
          label={copy.filters.divisionsLabel}
          options={divisions}
          selectedIds={value.divisionIds}
          isDisabled={isBusy}
          isLoading={isLoading}
          allHint={copy.filters.allDivisionsHint}
          emptyStatement={copy.filters.divisionsEmpty}
          onSelectedIdsChange={(divisionIds): void =>
            onChange({...value, divisionIds})
          }
        />

        {/* No bulk action on the bunk plan: it is single-select (D-L). */}
        <FormControl maxW='260px'>
          <FormLabel fontSize='sm' htmlFor='tagged-photo-report-bunk-plan'>
            {copy.filters.bunkPlanLabel}
          </FormLabel>
          <Select
            id='tagged-photo-report-bunk-plan'
            isDisabled={isBusy}
            value={value.bunkPlanId === null ? '' : String(value.bunkPlanId)}
            onChange={(e): void =>
              onChange({
                ...value,
                bunkPlanId:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }>
            <option value=''>{copy.filters.bunkPlanNone}</option>
            {bunkPlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </Select>
          <Text fontSize='xs' color='gray.700' mt={1}>
            {copy.filters.bunkPlanHelp}
          </Text>
        </FormControl>
      </Flex>

      <Button
        type='submit'
        isDisabled={isLoading}
        isLoading={isRunning}
        data-testid='tagged-photo-report-submit'>
        {copy.filters.submit}
      </Button>
    </form>
  )
}
